import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { useFormPersistence } from '@/hooks/useFormPersistence';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Progress } from '@/components/ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronRight, ChevronLeft, User, Music, Target,
  Calendar, CheckCircle2, Loader2, Video, Clock,
  Dumbbell, Factory, Piano, Mic, Sparkles, GraduationCap, Gift,
  Download, Search, Home, ArrowRight
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import StudentWelcomePDF from '@/pdf/StudentWelcomePDF';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const COUNTRY_CODES = [
  { code: '+91', country: 'India' },
  { code: '+1', country: 'USA/Canada' },
  { code: '+44', country: 'UK' },
  { code: '+971', country: 'UAE' },
  { code: '+65', country: 'Singapore' },
  { code: '+61', country: 'Australia' },
  { code: '+49', country: 'Germany' },
  { code: '+33', country: 'France' },
  { code: '+81', country: 'Japan' },
  { code: '+86', country: 'China' },
  { code: '+82', country: 'South Korea' },
  { code: '+7', country: 'Russia' },
  { code: '+55', country: 'Brazil' },
  { code: '+27', country: 'South Africa' },
  { code: '+234', country: 'Nigeria' },
  { code: '+254', country: 'Kenya' },
  { code: '+20', country: 'Egypt' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+974', country: 'Qatar' },
  { code: '+60', country: 'Malaysia' },
  { code: '+63', country: 'Philippines' },
  { code: '+62', country: 'Indonesia' },
  { code: '+66', country: 'Thailand' },
  { code: '+84', country: 'Vietnam' },
  { code: '+880', country: 'Bangladesh' },
  { code: '+92', country: 'Pakistan' },
  { code: '+94', country: 'Sri Lanka' },
  { code: '+977', country: 'Nepal' },
];

const SIGNING_UP_FOR = [
  { value: 'myself', label: 'Myself' },
  { value: 'child', label: 'My child' },
  { value: 'friend_family', label: 'A friend or family member' },
];

const MUSICAL_JOURNEY = [
  { value: 'beginner_intermediate', label: 'Beginner - Intermediate (1–3 years of experience)' },
  { value: 'seasoned', label: 'Seasoned Musician (Recording or Performing Experience)' },
  { value: 'producer', label: 'Music Producer' },
  { value: 'educator', label: 'Music Educator' },
];

const PIANO_USAGE = [
  { value: 'singer_accompanist', label: 'Singer who accompanies myself' },
  { value: 'church_musician', label: 'Church Musician' },
  { value: 'band_player', label: 'Play in a Band' },
  { value: 'producer_daw', label: 'Music Producer and record in a DAW as a producer/arranger' },
  { value: 'classical_trained', label: 'Have studied classical piano from Trinity/ABRSM' },
  { value: 'self_taught', label: 'Self Taught Pianist' },
  { value: 'other', label: 'Other' },
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIMES = ['Morning (6am-12pm)', 'Afternoon (12pm-5pm)', 'Evening (5pm-9pm)', 'Night (9pm-12am)'];

const classroomIcons = {
  'music-gym': Dumbbell,
  'music-factory': Factory,
  'modular-piano': Piano,
  'theory-ear-training': Mic,
  'seasonal-genre': Calendar,
  'new-offerings': Sparkles,
  'foundation-lessons': GraduationCap,
  'perks-tiers': Gift,
};

const STEPS = [
  { id: 1, title: 'Basic Info', icon: User },
  { id: 2, title: 'Musical Background', icon: Music },
  { id: 3, title: 'Select Classes', icon: CheckCircle2 },
  { id: 4, title: 'Goals', icon: Target },
  { id: 5, title: 'Schedule', icon: Calendar },
];

export const NewStudentForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [classrooms, setClassrooms] = useState([]);
  const [expandedClassroom, setExpandedClassroom] = useState(null);
  
  const [formData, setFormData, clearSavedData] = useFormPersistence('new_student', {
    full_name: '',
    email: '',
    phone_country_code: '+91',
    phone_number: '',
    city: '',
    country: '',
    signing_up_for: '',
    musical_journey: '',
    piano_usage: [],
    piano_usage_other: '',
    class_interests: [],
    goals: '',
    songs_to_learn: '',
    technical_challenges: '',
    preferred_days: [],
    preferred_times: [],
    interested_in_person: false,
  });

  useEffect(() => {
    fetchClassrooms();
  }, []);

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`);
      setClassrooms(response.data);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
      toast.error('Failed to load class options');
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const toggleClassInterest = (classData, classroomName) => {
    const existingIndex = formData.class_interests.findIndex(
      ci => ci.class_id === classData.id
    );
    
    if (existingIndex >= 0) {
      setFormData(prev => ({
        ...prev,
        class_interests: prev.class_interests.filter(ci => ci.class_id !== classData.id)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        class_interests: [...prev.class_interests, {
          class_id: classData.id,
          class_name: classData.name,
          classroom_name: classroomName,
          prefer_recording: false
        }]
      }));
    }
  };

  const toggleRecordingPreference = (classId) => {
    setFormData(prev => ({
      ...prev,
      class_interests: prev.class_interests.map(ci =>
        ci.class_id === classId ? { ...ci, prefer_recording: !ci.prefer_recording } : ci
      )
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.full_name && formData.email && formData.phone_number && 
               formData.city && formData.country;
      case 2:
        return formData.signing_up_for && formData.musical_journey && formData.piano_usage.length > 0;
      case 3:
        return formData.class_interests.length > 0;
      case 4:
        return formData.goals;
      case 5:
        return formData.preferred_days.length > 0 && formData.preferred_times.length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/students/new`, formData);
      clearSavedData();
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (currentStep / 5) * 100;

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const blob = await pdf(
        <StudentWelcomePDF formData={formData} studentType="new" classrooms={classrooms} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NSM_Course_Selection_${formData.full_name.replace(/\s+/g, '_')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="glass-card rounded-2xl p-10">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-emerald-400" size={40} />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#EDEDED] mb-4">
                Application Submitted!
              </h1>
              <p className="text-[#A1A1AA] text-lg mb-8">
                Thank you, {formData.full_name.split(' ')[0]}! We've received your application and will be in touch within 48 hours to discuss your personalized semester plan.
              </p>

              <div className="glass-card rounded-xl p-6 mb-8 border border-[#D4AF37]/30">
                <h3 className="font-heading text-xl text-[#F5F5DC] mb-3">Download Your Course Selection Guide</h3>
                <p className="text-[#A1A1AA] text-sm mb-6">
                  A personalized PDF with your selected classes, goals, schedule preferences, and what to expect next. Keep it as a reference!
                </p>
                <Button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="btn-gold rounded-full px-8 py-4 text-lg"
                >
                  {isGeneratingPDF ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download size={20} className="mr-2" />
                      Download PDF Guide
                    </>
                  )}
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="/track">
                  <Button variant="outline" className="rounded-full px-6 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                    <Search size={16} className="mr-2" />
                    Track Your Application
                  </Button>
                </a>
                <a href="/">
                  <Button variant="outline" className="rounded-full px-6 border-white/20 text-[#EDEDED] hover:bg-white/10">
                    <Home size={16} className="mr-2" />
                    Back to Home
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              New Student Application
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-4">
              Customize Your Semester
            </h1>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Welcome to Nathaniel School of Music! Tell us about yourself and your musical goals,
              and we'll craft the perfect learning journey for you.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between mb-4">
              {STEPS.map((step) => (
                <div 
                  key={step.id}
                  className={`flex flex-col items-center ${
                    step.id <= currentStep ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    step.id < currentStep 
                      ? 'bg-[#D4AF37] text-black'
                      : step.id === currentStep 
                        ? 'border-2 border-[#D4AF37] text-[#D4AF37]'
                        : 'border border-[#2A2A2A] text-[#A1A1AA]'
                  }`}>
                    {step.id < currentStep ? (
                      <CheckCircle2 size={20} />
                    ) : (
                      <step.icon size={18} />
                    )}
                  </div>
                  <span className="text-xs hidden md:block">{step.title}</span>
                </div>
              ))}
            </div>
            <Progress value={progress} className="h-2 bg-[#2A2A2A]" />
          </div>

          {/* Form Container */}
          <div className="glass-card rounded-2xl p-8">
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Tell us about yourself</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-[#F5F5DC]">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => updateFormData('full_name', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED]"
                    placeholder="Enter your full name"
                    data-testid="input-full-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#F5F5DC]">Email ID *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED]"
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-[#F5F5DC]">WhatsApp Number *</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={formData.phone_country_code}
                      onValueChange={(value) => updateFormData('phone_country_code', value)}
                    >
                      <SelectTrigger className="w-[140px] bg-black/50 border-white/10 text-[#EDEDED]" data-testid="select-country-code">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {COUNTRY_CODES.map(({ code, country }) => (
                          <SelectItem key={code} value={code} className="text-[#EDEDED]">
                            {code} ({country})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={formData.phone_number}
                      onChange={(e) => updateFormData('phone_number', e.target.value)}
                      className="flex-1 bg-black/50 border-white/10 text-[#EDEDED]"
                      placeholder="Phone number"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-[#F5F5DC]">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => updateFormData('city', e.target.value)}
                      className="bg-black/50 border-white/10 text-[#EDEDED]"
                      placeholder="Your city"
                      data-testid="input-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-[#F5F5DC]">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => updateFormData('country', e.target.value)}
                      className="bg-black/50 border-white/10 text-[#EDEDED]"
                      placeholder="Your country"
                      data-testid="input-country"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Musical Background */}
            {currentStep === 2 && (
              <div className="space-y-8 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Your Musical Background</h2>
                
                <div className="space-y-4">
                  <Label className="text-[#F5F5DC]">Who are you signing up for? *</Label>
                  <RadioGroup
                    value={formData.signing_up_for}
                    onValueChange={(value) => updateFormData('signing_up_for', value)}
                    className="space-y-3"
                  >
                    {SIGNING_UP_FOR.map(option => (
                      <div key={option.value} className="flex items-center space-x-3 p-4 rounded-lg border border-white/10 hover:border-[#D4AF37]/50 transition-colors">
                        <RadioGroupItem value={option.value} id={option.value} data-testid={`radio-signup-${option.value}`} />
                        <Label htmlFor={option.value} className="text-[#EDEDED] cursor-pointer flex-1">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-4">
                  <Label className="text-[#F5F5DC]">Where are you in your musical journey? *</Label>
                  <RadioGroup
                    value={formData.musical_journey}
                    onValueChange={(value) => updateFormData('musical_journey', value)}
                    className="space-y-3"
                  >
                    {MUSICAL_JOURNEY.map(option => (
                      <div key={option.value} className="flex items-center space-x-3 p-4 rounded-lg border border-white/10 hover:border-[#D4AF37]/50 transition-colors">
                        <RadioGroupItem value={option.value} id={`journey-${option.value}`} data-testid={`radio-journey-${option.value}`} />
                        <Label htmlFor={`journey-${option.value}`} className="text-[#EDEDED] cursor-pointer flex-1">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-4">
                  <Label className="text-[#F5F5DC]">How do you usually play or use the piano? (Select all that apply) *</Label>
                  <div className="space-y-3">
                    {PIANO_USAGE.map(option => (
                      <div 
                        key={option.value} 
                        className={`flex items-center space-x-3 p-4 rounded-lg border transition-colors cursor-pointer ${
                          formData.piano_usage.includes(option.value) 
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                            : 'border-white/10 hover:border-[#D4AF37]/50'
                        }`}
                        onClick={() => toggleArrayItem('piano_usage', option.value)}
                      >
                        <Checkbox 
                          checked={formData.piano_usage.includes(option.value)}
                          data-testid={`checkbox-usage-${option.value}`}
                        />
                        <Label className="text-[#EDEDED] cursor-pointer flex-1">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  
                  {formData.piano_usage.includes('other') && (
                    <Input
                      value={formData.piano_usage_other}
                      onChange={(e) => updateFormData('piano_usage_other', e.target.value)}
                      className="bg-black/50 border-white/10 text-[#EDEDED] mt-2"
                      placeholder="Please specify..."
                      data-testid="input-usage-other"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Class Selection */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fade-in-up">
                <div>
                  <h2 className="font-heading text-2xl text-[#F5F5DC] mb-2">Select Your Classes</h2>
                  <p className="text-[#A1A1AA] text-sm mb-2">
                    Choose the classes you're interested in. Durations vary from 30-90 minutes. 
                    We'll contact you within 48 hours with a personalized course plan.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-[#D4AF37] bg-[#D4AF37]/10 p-3 rounded-lg">
                    <Video size={16} />
                    <span>Can't attend live? Mark classes for HD Recording & Study Material</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {classrooms.map(classroom => {
                    const IconComponent = classroomIcons[classroom.id] || Music;
                    const isExpanded = expandedClassroom === classroom.id;
                    
                    return (
                      <div key={classroom.id} className="border border-white/10 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedClassroom(isExpanded ? null : classroom.id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                          data-testid={`classroom-toggle-${classroom.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                              <IconComponent className="text-[#D4AF37]" size={20} />
                            </div>
                            <div className="text-left">
                              <h3 className="font-heading text-[#F5F5DC]">{classroom.name}</h3>
                              <p className="text-[#A1A1AA] text-xs">{classroom.classes?.length || 0} classes</p>
                            </div>
                          </div>
                          <ChevronRight className={`text-[#A1A1AA] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {isExpanded && (
                          <div className="border-t border-white/10 p-4 space-y-3 bg-black/20">
                            {classroom.classes?.map(cls => {
                              const isSelected = formData.class_interests.some(ci => ci.class_id === cls.id);
                              const classInterest = formData.class_interests.find(ci => ci.class_id === cls.id);
                              
                              return (
                                <div
                                  key={cls.id}
                                  className={`p-4 rounded-lg border transition-all ${
                                    isSelected
                                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                                      : 'border-white/10 hover:border-[#D4AF37]/30'
                                  }`}
                                >
                                  <div 
                                    className="flex items-start gap-3 cursor-pointer"
                                    onClick={() => toggleClassInterest(cls, classroom.name)}
                                  >
                                    <Checkbox 
                                      checked={isSelected}
                                      className="mt-1"
                                      data-testid={`checkbox-class-${cls.id}`}
                                    />
                                    <div className="flex-1">
                                      <h4 className="text-[#F5F5DC] font-medium">{cls.name}</h4>
                                      <p className="text-[#A1A1AA] text-sm mt-1">{cls.description}</p>
                                      {cls.duration_minutes && (
                                        <div className="flex items-center gap-1 mt-2 text-xs text-[#A1A1AA]">
                                          <Clock size={12} />
                                          <span>{cls.duration_minutes} minutes</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {isSelected && (
                                    <div className="mt-4 pt-4 border-t border-white/10">
                                      <div 
                                        className="flex items-center gap-3 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRecordingPreference(cls.id);
                                        }}
                                      >
                                        <Checkbox 
                                          checked={classInterest?.prefer_recording || false}
                                          data-testid={`checkbox-recording-${cls.id}`}
                                        />
                                        <div className="flex items-center gap-2">
                                          <Video size={14} className="text-[#D4AF37]" />
                                          <span className="text-sm text-[#A1A1AA]">
                                            Rely on HD Recording & Study Material (Can't attend live)
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {formData.class_interests.length > 0 && (
                  <div className="bg-[#121212] rounded-lg p-4">
                    <h4 className="text-[#D4AF37] font-medium mb-2">
                      Selected Classes ({formData.class_interests.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {formData.class_interests.map(ci => (
                        <span 
                          key={ci.class_id}
                          className="px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm"
                        >
                          {ci.class_name}
                          {ci.prefer_recording && ' (Recording)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Goals */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Your Musical Goals</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="goals" className="text-[#F5F5DC]">What are your musical goals? *</Label>
                  <Textarea
                    id="goals"
                    value={formData.goals}
                    onChange={(e) => updateFormData('goals', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[120px]"
                    placeholder="Tell us what you want to achieve with your piano learning..."
                    data-testid="textarea-goals"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="songs" className="text-[#F5F5DC]">Songs you want to learn</Label>
                  <Textarea
                    id="songs"
                    value={formData.songs_to_learn}
                    onChange={(e) => updateFormData('songs_to_learn', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[100px]"
                    placeholder="List any specific songs or artists you'd like to learn..."
                    data-testid="textarea-songs"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="challenges" className="text-[#F5F5DC]">Technical challenges you face</Label>
                  <Textarea
                    id="challenges"
                    value={formData.technical_challenges}
                    onChange={(e) => updateFormData('technical_challenges', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[100px]"
                    placeholder="What technical aspects do you struggle with? (e.g., hand independence, rhythm, sight reading...)"
                    data-testid="textarea-challenges"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Schedule */}
            {currentStep === 5 && (
              <div className="space-y-8 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Schedule Preferences</h2>
                
                <div className="space-y-4">
                  <Label className="text-[#F5F5DC]">Preferred Days *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {DAYS.map(day => (
                      <div
                        key={day}
                        onClick={() => toggleArrayItem('preferred_days', day)}
                        className={`p-3 rounded-lg border text-center cursor-pointer transition-all ${
                          formData.preferred_days.includes(day)
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/50'
                        }`}
                        data-testid={`day-${day.toLowerCase()}`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-[#F5F5DC]">Preferred Times *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {TIMES.map(time => (
                      <div
                        key={time}
                        onClick={() => toggleArrayItem('preferred_times', time)}
                        className={`p-3 rounded-lg border text-center cursor-pointer transition-all ${
                          formData.preferred_times.includes(time)
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/50'
                        }`}
                        data-testid={`time-${time.split(' ')[0].toLowerCase()}`}
                      >
                        {time}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div 
                    className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.interested_in_person
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-white/10 hover:border-[#D4AF37]/50'
                    }`}
                    onClick={() => updateFormData('interested_in_person', !formData.interested_in_person)}
                  >
                    <Checkbox 
                      checked={formData.interested_in_person}
                      data-testid="checkbox-in-person"
                    />
                    <div>
                      <Label className="text-[#F5F5DC] cursor-pointer">
                        I'm interested in in-person sessions at the Bangalore studio
                      </Label>
                      <p className="text-[#A1A1AA] text-sm">
                        Access our high-quality recording studio and jam room
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              {currentStep > 1 ? (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  className="border-white/20 text-[#EDEDED] hover:bg-white/5"
                  data-testid="btn-previous"
                >
                  <ChevronLeft className="mr-2" size={18} />
                  Previous
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  className="btn-gold rounded-full px-8"
                  data-testid="btn-next"
                >
                  Next Step
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="btn-gold rounded-full px-8"
                  data-testid="btn-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <CheckCircle2 className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NewStudentForm;
